import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react"
import classNames from "classnames"
import { Dropdown, Container, Offcanvas, Stack, Navbar, Spinner } from "react-bootstrap"
import React, { useEffect, useState } from "react"
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom"
import { MessageReporter } from "../components/MessageReporter"
import { useRole } from "../components/RoleContext"
import { Announcement } from "../components/Announcement"
import { ApiClient } from "../ApiClient"
import { Permission } from "../api"
import { hasAnyRequiredPermission } from "../components/Authorization/RoleProtect"

const menuItems = [
    {
        path: "/",
        name: "Home",
        icon: "fa-solid fa-home",
        requiredAnyOfPermissions: [Permission.GetAnyResourceMetrics],
    },
    {
        path: "/system",
        name: "Systems",
        icon: "fa-solid fa-network-wired",
        requiredAnyOfPermissions: [Permission.GetAnyResourceSystem],
    },
    {
        path: "/device",
        name: "Devices",
        icon: "fa-regular fa-hard-drive",
        requiredAnyOfPermissions: [Permission.GetAnyResourceDevice],
    },
    {
        path: "/devicegroup",
        name: "Device groups",
        icon: "fa-regular fa-rectangle-list",
        requiredAnyOfPermissions: [Permission.GetAnyResourceFirmware],
    },
    {
        path: "/firmware",
        name: "Firmware",
        icon: "fa-regular fa-file-code",
        requiredAnyOfPermissions: [Permission.GetAnyResourceFirmware],
    },
    {
        path: "/manufacturing",
        name: "Manufacturing",
        icon: "fa-solid fa-industry",
        requiredAnyOfPermissions: [Permission.GetAnyResourceManufacturing],
    },
    {
        path: "/remotelogging",
        name: "Remote logging",
        icon: "fa-solid fa-stroopwafel",
        requiredAnyOfPermissions: [Permission.GetAnyResourceConfRemoteLogging],
    },
    {
        path: "/user",
        name: "Users",
        icon: "fa-regular fa-user",
        requiredAnyOfPermissions: [Permission.GetAnyResourceUser],
    },
    {
        path: "/translations",
        name: "Translations",
        icon: "fa-regular fa-keyboard",
        requiredAnyOfPermissions: [Permission.GetAnyResourceTranslation],
    },
    {
        path: "/roles",
        name: "Roles",
        icon: "fa-solid fa-shield",
        requiredAnyOfPermissions: [Permission.GetAnyResourceRoles],
    },
]

export const Layout = withAuthenticationRequired(() => {
    const { permissions, role } = useRole()
    const location = useLocation()
    const navigate = useNavigate()

    const [hasNoPermissions, setHasNoPermissions] = useState(false)

    useEffect(() => {
        if (!permissions || !location || !navigate) {
            return
        }

        const currentPath = location.pathname
        const matchedMenuItem = menuItems.find((item) => item.path === currentPath)

        if (
            matchedMenuItem &&
            !hasAnyRequiredPermission(matchedMenuItem.requiredAnyOfPermissions, permissions)
        ) {
            const firstAllowedMenuItem = menuItems.find((item) =>
                hasAnyRequiredPermission(item.requiredAnyOfPermissions, permissions)
            )

            if (firstAllowedMenuItem && firstAllowedMenuItem.path !== currentPath) {
                navigate(firstAllowedMenuItem.path, { replace: true })
            }

            setHasNoPermissions(!firstAllowedMenuItem)
        } else {
            setHasNoPermissions(false)
        }
    }, [role, location, permissions, navigate])

    return (
        <>
            <MobileMenu role={role} permissions={permissions} />
            <SideBar role={role} permissions={permissions} />
            <div className="partial-background">
                <div className="m-3 m-lg-4">
                    <main className="container-lg">
                        <Announcement />
                        {hasNoPermissions ? (
                            <UnauthorizedMessage />
                        ) : role === undefined ? (
                            <Spinner />
                        ) : (
                            <Outlet />
                        )}
                    </main>
                    <footer className="m-3 m-lg-4 text-muted"></footer>
                </div>
            </div>
            <MessageReporter />
        </>
    )
})

const MobileMenu = ({ role, permissions }) => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

    if (!role) {
        return <></>
    }

    return (
        <>
            <Navbar expand="xs" className="d-lg-none bg-white">
                <Container fluid>
                    <BrandImage height="50px" />
                    <Navbar.Toggle onClick={() => setMobileMenuOpen(true)} />
                </Container>
            </Navbar>
            <Offcanvas show={mobileMenuOpen} onHide={() => setMobileMenuOpen(false)}>
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>
                        <BrandImage height="50px" />
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <Stack as="nav" gap={2} className="p-3">
                        {menuItems
                            .filter((c) =>
                                hasAnyRequiredPermission(c.requiredAnyOfPermissions, permissions)
                            )
                            .map((item) => (
                                <NavItem
                                    key={item.path}
                                    item={item}
                                    onClick={() => setMobileMenuOpen(false)}
                                />
                            ))}
                        <hr className="border-secondary" />
                        <ProfileBtn />
                    </Stack>
                </Offcanvas.Body>
            </Offcanvas>
        </>
    )
}

const SideBar = ({ role, permissions }) => {
    const [showTranslations, setShowTranslations] = useState(false)
    const { getAccessTokenSilently } = useAuth0()

    useEffect(() => {
        if (!role) {
            // Don't load can-edit until the role is loaded
            return
        }
        getAccessTokenSilently()
            .then((token) =>
                ApiClient.get("/v2/management/can-edit", {
                    Authorization: "Bearer " + token,
                })
            )
            .then((res) => {
                if (res.data.can_edit) {
                    setShowTranslations(true)
                }
            })
            .catch((err) => {})
    }, [setShowTranslations, getAccessTokenSilently, role])

    if (!role) {
        return <></>
    }

    return (
        <Stack as="nav" gap={2} className="sidebar p-3 d-none d-lg-flex bg-white shadow-sm h-100">
            <BrandImage />
            <hr className="border-secondary" />
            {menuItems
                .filter((item) => item.name !== "Translations" || showTranslations)
                .filter((c) => hasAnyRequiredPermission(c.requiredAnyOfPermissions, permissions))
                .map((item) => (
                    <NavItem key={item.path} item={item} />
                ))}
            <div className="flex-grow-1" />
            <ProfileBtn />
        </Stack>
    )
}

const BrandImage = (props) => (
    <Link className="navbar-brand" to="/">
        <img {...props} className="d-block w-75 mx-auto" alt="Ben logo" src="/ben.png" />
    </Link>
)

const NavItem = ({ item, onClick }) => (
    <NavLink
        key={item.path}
        onClick={onClick}
        to={item.path}
        className={({ isActive }) =>
            classNames("btn btn-light text-start", { "text-muted": !isActive })
        }
    >
        <i className={`${item.icon} fa-fw me-2`} />
        {item.name}
    </NavLink>
)

const ProfileBtn = () => {
    const { user, isLoading, isAuthenticated, logout } = useAuth0()
    const doLogout = () => logout({ logoutParams: { returnTo: window.location.origin } })

    return (
        <Dropdown className="profile-btn">
            <Dropdown.Toggle
                variant="light"
                className="text-start text-muted text-truncate w-100 caret-off"
            >
                <i className="fa-regular fa-user me-2" />
                {isLoading || !isAuthenticated ? "Loading..." : user.nickname}
            </Dropdown.Toggle>

            <Dropdown.Menu>
                <Dropdown.Item onClick={doLogout}>Logout</Dropdown.Item>
            </Dropdown.Menu>
        </Dropdown>
    )
}

const UnauthorizedMessage = () => (
    <div className="content-section">
        You do not have access to this application.
        <br />
        Please contact support if you need access.
    </div>
)
